import { EmptyAndMissingFile } from '@/assets';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { PdfViewer } from '@/components/pdfViewer';
import { usePaymentDetail } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { useFileInfoDrawerContext } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/context/FileInfoDrawerContext';
import { Header } from '@/components/InvoiceReportDetailDrawer/RightContent/Preview/Header';
import { Loading } from '@moneyforward/ap-frontend-components';
import { useGetMfFileAndObjectURL } from '@service/mf_file/MfFile.service';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export type MainPreviewProps = {
  mfFileId?: string;
};

const SuspenseLoading: FC = memo(() => {
  return (
    <div>
      <Loading className={cx(styles['spinner'])} size='large' />
    </div>
  );
});
SuspenseLoading.displayName = 'SuspenseLoading';

export const MainPreview: FC<MainPreviewProps> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<SuspenseLoading />}>
        <InnerMainPreview {...props} />
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
MainPreview.displayName = 'MainPreview';

export const InnerMainPreview: FC<MainPreviewProps> = memo(({ mfFileId }) => {
  const { pdfViewerProps } = usePaymentDetail();
  const { onOpen } = useFileInfoDrawerContext();

  // Use useGetMfFileAndObjectURL to fetch PDF URL
  const { url } = useGetMfFileAndObjectURL(mfFileId || '');

  const handleOpenFileInfoDrawer = useCallback(() => {
    onOpen();
  }, [onOpen]);

  return (
    <div className={cx(styles['previewContainer'])}>
      {pdfViewerProps && (
        <Header
          {...pdfViewerProps}
          onOpenFileInfoDrawer={handleOpenFileInfoDrawer}
        />
      )}
      <div className={cx(styles['pdfViewerContainer'])}>
        <PdfViewer key={`business-${url}`} src={url} />
      </div>
    </div>
  );
});
InnerMainPreview.displayName = 'InnerMainPreview';

export const EmptyPreview: FC = memo(() => {
  const { pdfViewerProps } = usePaymentDetail();
  const src = EmptyAndMissingFile;

  return (
    <div className={cx(styles['previewContainer'])}>
      {pdfViewerProps && <Header {...pdfViewerProps} isEmpty />}
      <PdfViewer key={src} src={src} />
    </div>
  );
});
EmptyPreview.displayName = 'EmptyPreview';
